/**
 * PATH: packages/ui/src/qa/QaPhotoGallery.tsx
 * FILE_ID: FE-QA-PHOTO-GALLERY-001
 * PURPOSE: QA photo row that reads shared size/Fit/pan; chrome only when unhosted.
 * LANDMINE: Never let a failed img fall back to bare alt text — that painted
 *      the item title as a card. Failed and null src must use explicit tiles.
 */

import { QaPhotoDisplayControls } from './QaPhotoDisplayControls'
import { QaPhotoTile } from './QaPhotoTile'
import { QaPhotoPrefsProvider, useQaPhotoPrefs, useQaPhotoPrefsHosted } from './qaPhotoPrefs'
import type { QaGalleryPhoto } from './qaPhotoView'

export type { QaGalleryPhoto }

export interface QaPhotoGalleryProps {
  photos: QaGalleryPhoto[]
  onDelete?: (id: string) => void
  onOpen?: (index: number) => void
  className?: string
}

function QaPhotoGalleryFrame({
  photos,
  onDelete,
  onOpen,
  className,
  showChrome,
}: QaPhotoGalleryProps & { showChrome: boolean }) {
  const prefs = useQaPhotoPrefs()
  if (photos.length === 0) return null

  return (
    <div className={className}>
      {showChrome ? <div className="mb-2"><QaPhotoDisplayControls /></div> : null}
      <ul className="flex flex-wrap items-start gap-2">
        {photos.map((photo, index) => (
          <QaPhotoTile
            key={photo.id}
            photo={photo}
            view={prefs.viewOf(photo.id)}
            selectEnabled={prefs.selectEnabled}
            selected={prefs.isSelected(photo.id)}
            onToggleSelected={() => prefs.toggleSelected(photo.id)}
            onPan={(x, y) => prefs.setPan(photo.id, x, y)}
            onOpen={onOpen ? () => onOpen(index) : undefined}
            onDelete={onDelete ? () => onDelete(photo.id) : undefined}
          />
        ))}
      </ul>
    </div>
  )
}

export function QaPhotoGallery(props: QaPhotoGalleryProps) {
  const hosted = useQaPhotoPrefsHosted()
  const body = <QaPhotoGalleryFrame {...props} showChrome={!hosted} />
  return hosted ? body : <QaPhotoPrefsProvider>{body}</QaPhotoPrefsProvider>
}
